import MarketingShell from '../../components/marketing-shell/MarketingShell'
import MarketingIcon from '../../components/marketing-icon/MarketingIcon'
import MarketingCtaStrip from '../../components/marketing-cta-strip/MarketingCtaStrip'
import { GET_STARTED_URL } from '../../_data/marketingLinks'
import {
  MARKETING_BENEFITS,
  MARKETING_FEATURES,
  MARKETING_USE_CASES,
} from '../../_data/marketingContent'
import {
  BenefitBody,
  BenefitCard,
  BenefitNumber,
  BenefitTitle,
  BenefitsGrid,
  FeatureBody,
  FeatureCard,
  FeatureTitle,
  FeaturesGrid,
  Hero,
  HeroAccent,
  HeroActions,
  HeroCopy,
  HeroEyebrow,
  HeroFinePrint,
  HeroHeadline,
  HeroInner,
  HeroSubhead,
  HeroVisual,
  PlaceholderHint,
  PlaceholderLabel,
  PrimaryCta,
  ProductPreviewPlaceholder,
  SectionEyebrow,
  SectionFooterLink,
  SectionHead,
  SectionInner,
  SectionShell,
  SectionSubhead,
  SectionTitle,
  SecondaryCta,
  UseCasePill,
  UseCasePillTitle,
  UseCaseStrip,
} from './styled'

const HomePage = (): JSX.Element => (
  <MarketingShell>
    <Hero>
      <HeroInner>
        <HeroCopy>
          <HeroEyebrow>QuickCal · from QuickCasa</HeroEyebrow>
          <HeroHeadline>
            All the scheduling.
            <br />
            <HeroAccent>None of the seat math.</HeroAccent>
          </HeroHeadline>
          <HeroSubhead>
            QuickCal is QuickCasa&apos;s native scheduling product. Set your
            hours once, share a link, and watch the right slots get picked —
            without a per-seat tax for every teammate you add.
          </HeroSubhead>
          <HeroActions>
            <PrimaryCta href={GET_STARTED_URL}>Get started</PrimaryCta>
            <SecondaryCta to="/features">See features</SecondaryCta>
          </HeroActions>
          <HeroFinePrint>
            Five-minute setup · Two-way Google &amp; Microsoft sync · Live the
            same day
          </HeroFinePrint>
        </HeroCopy>
        <HeroVisual>
          <ProductPreviewPlaceholder>
            <PlaceholderLabel>Product preview goes here</PlaceholderLabel>
            <PlaceholderHint>
              Drop a real QuickCal booking-page screenshot, looped GIF, or
              short MP4 in this slot.
            </PlaceholderHint>
          </ProductPreviewPlaceholder>
        </HeroVisual>
      </HeroInner>
    </Hero>

    <SectionShell background="muted">
      <SectionInner>
        <SectionHead>
          <SectionEyebrow>Why QuickCal</SectionEyebrow>
          <SectionTitle>Booking that earns its place on the team.</SectionTitle>
          <SectionSubhead>
            QuickCal exists because the off-the-shelf options either nickel-and-
            dime your team or stop short of features that actually matter. We
            picked a different tradeoff.
          </SectionSubhead>
        </SectionHead>
        <BenefitsGrid>
          {MARKETING_BENEFITS.map((benefit, index) => (
            <BenefitCard key={benefit.key}>
              <BenefitNumber>0{index + 1}</BenefitNumber>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitBody>{benefit.body}</BenefitBody>
            </BenefitCard>
          ))}
        </BenefitsGrid>
      </SectionInner>
    </SectionShell>

    <SectionShell background="surface">
      <SectionInner>
        <SectionHead>
          <SectionEyebrow>Features</SectionEyebrow>
          <SectionTitle>
            Every booking feature that actually matters.
          </SectionTitle>
          <SectionSubhead>
            The list is not longer just to be longer. Each one earns its keep
            on the booking flow, on the calendar, or on the team page.
          </SectionSubhead>
        </SectionHead>
        <FeaturesGrid>
          {MARKETING_FEATURES.map((feature) => (
            <FeatureCard key={feature.key}>
              <MarketingIcon kind={feature.iconKind} />
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureBody>{feature.body}</FeatureBody>
            </FeatureCard>
          ))}
        </FeaturesGrid>
        <SectionFooterLink to="/features">
          See the full feature breakdown →
        </SectionFooterLink>
      </SectionInner>
    </SectionShell>

    <SectionShell background="muted">
      <SectionInner>
        <SectionHead>
          <SectionEyebrow>Use cases</SectionEyebrow>
          <SectionTitle>
            Built for anyone who books on a calendar.
          </SectionTitle>
          <SectionSubhead>
            QuickCal started inside a real-estate platform, but the booking
            engine underneath has very little opinion about who&apos;s on the
            other end of the link.
          </SectionSubhead>
        </SectionHead>
        <UseCaseStrip>
          {MARKETING_USE_CASES.map((useCase) => (
            <UseCasePill key={useCase.key} to="/use-cases">
              <MarketingIcon kind={useCase.iconKind} size="small" />
              <UseCasePillTitle>{useCase.title}</UseCasePillTitle>
            </UseCasePill>
          ))}
        </UseCaseStrip>
        <SectionFooterLink to="/use-cases">
          See use cases in detail →
        </SectionFooterLink>
      </SectionInner>
    </SectionShell>

    <MarketingCtaStrip
      eyebrow="Ready when you are"
      headline="Stop letting a scheduling tool tax your team."
      body="Spin up your first booking page in five minutes. Add the rest of the team without watching a seat counter."
      secondaryLabel="Browse features"
      secondaryHref="/features"
    />
  </MarketingShell>
)

export default HomePage
